import { observer } from "mobx-react-lite";
import { useTranslations } from "next-intl";

import { Icon } from "@/components/icons/Icon";
import { Sidebar } from "@/components/sidebar";
import { Dropdown, type DropdownItem } from "@/components/sidebar/Dropdown";
import { preserveMathCursorPosition } from "@/components/tiptap/utils/preserveMathCursorPosition";
import { type DocumentModel } from "@/models/DocumentModel";

import { CollapsedPopover } from "../CollapsedPopover";
import { appendBlock } from "../create-blocks/utils/appendBlock";

type ListType = "bulletList" | "orderedList";

type Props = {
  doc: DocumentModel;
  isCollapsed?: boolean;
  disabled?: boolean;
};

export const ListFormatDropdown = observer(function ListFormatDropdown({
  doc,
  isCollapsed,
  disabled,
}: Props) {
  const t = useTranslations("sidebar.createBlocks");
  const { tiptap, editorState } = doc.editor;

  if (!tiptap) {
    return null;
  }

  const handleSelect = (type: ListType) => {
    if (editorState.isNonConvertableBlock) {
      appendBlock(doc, type);
      return;
    }

    preserveMathCursorPosition(doc, () => {
      if (type === "bulletList") {
        tiptap.commands.toggleBulletList();
        return;
      }

      if (type === "orderedList") {
        tiptap.commands.toggleOrderedList();
      }
    });
  };

  const items = [
    {
      title: t("bulletList.title"),
      icon: <Icon name="block-format/Bullet" />,
      active: editorState.isBulletList,
      testId: "bullet-list-block-type",
      onClick: () => {
        handleSelect("bulletList");
      },
    },
    {
      title: t("numberedList.title"),
      icon: <Icon name="block-format/Number" />,
      active: editorState.isOrderedList,
      testId: "numbered-list-block-type",
      onClick: () => {
        handleSelect("orderedList");
      },
    },
  ] satisfies DropdownItem[];

  const activeIcon = items.find(({ active }) => active)?.icon || <Icon name="block-format/List" />;
  const isListActive = editorState.isBulletList || editorState.isOrderedList;

  if (isCollapsed) {
    return (
      <CollapsedPopover
        icon={activeIcon}
        tooltipTitle={t("list.title")}
        isActive={isListActive}
        items={items}
        itemsRole="radio"
      />
    );
  }

  return (
    <Dropdown
      items={items}
      itemsRole="menuitemradio"
      disabled={disabled}
      toggleButtonComponent={
        <Sidebar.ToggleButton
          as="div"
          tooltipTitle={t("list.title")}
          isActive={isListActive}
          icon={activeIcon}
          hintKey="list"
          disabled={disabled}
        />
      }
    />
  );
});
